<template>
    <div class="info-window pt-1 pb-2 pl-1 pr-2">
        {{ /* Title */ }}
        <div class="d-flex mb-2">
            <h4
                class="info-window-title mr-3 my-0 d-inline"
                @click="handleTitleClick(getActiveStation)"
            >
                {{activeStationName}}
            </h4>
            <span class="popup-small-grey-text">
                {{infoWindowPosition.lat}}&deg;N, {{infoWindowPosition.lng}}&deg;E
            </span>
        </div>

        <hr class="info-window-hr mt-0 mb-1"/>

        {{ /* Current update */ }}
        <span class="d-inline-block text-muted small mb-2">
            {{ this.$i18n.t('dashboard.infoWindow.currentUpdate' )}}: {{ infoPoint.lastUpdated || '-' }}
        </span>

        {{ /* Sensors icons */ }}
        <MapPopupIcons :data="sensors"></MapPopupIcons>
        <div class="py-2 px-2" v-if="isInactive">{{ $t('dashboard.not-monitored') }}</div>

        {{ /* Mayer contact */ }}
        <span class="d-block my-2">
            {{ this.$i18n.t('dashboard.infoWindow.contact') }}: {{ infoPoint.phone || '-' }}
        </span>

        {{ /* Action tab with buttons */ }}
        <div class="d-flex mb-2 p-1">
            <WhiteIconButton
                icon="phone"
                size="small"
                class="info-window-btn"
                :text="this.$i18n.t('dashboard.infoWindow.call')"
                :href="infoPoint.phone ? `tel:${infoPoint.phone}` : ''"
                :disabled="!(infoPoint.phone)"
            />
            <WhiteIconButton
                icon="mail"
                size="small"
                class="info-window-btn"
                :text="this.$i18n.t('dashboard.infoWindow.sendEmail')"
                :href="infoPoint.email ? `mailto:${infoPoint.email}` : ''"
                :disabled="!(infoPoint.email)"
            />
        </div>

        <hr class="mt-0 mb-2"/>

        {{ /* Alarms list */ }}
        <div
            v-for="(iconKey, index) in alarms"
            :key="index"
            class="d-flex mb-2"
        >
            <SvgIcon :name="iconKey.icon"/>
            <span class="ml-2">{{ alarmText(iconKey) }}</span>
        </div>
    </div>

</template>

<script>
    // constants
    import {excludedSensors, iconsUnits} from "../../constants/sensors-constants";
    import {INACTIVE_STATUS, iconColors, OVERFLOWING_WATER_LEVEL} from "../../constants/map-constants";
    // vuex
    import {mapGetters, mapActions} from "vuex";
    import {SET_CURRENT_DAM} from '../../store/actions/dashboard';
    // components
    import MapPopupIcons from "./MapPopupIcons";
    import WhiteIconButton from '../shared/WhiteIconButton';
    import SvgIcon from "../shared/SvgIcon";
    // mixins
    import SafeTranslateMixin from "../../mixins/SafeTranslateMixin";
    // modules
    import { isEmpty } from 'lodash'
    // utils
    import {formatDate, parsedDate} from "../../utils/common";

    const DEFAULT = 'default';

    export default {
        name: "InfoWindow",
        components: {MapPopupIcons, WhiteIconButton, SvgIcon},
        mixins: [SafeTranslateMixin],
        data() {
            return {
                iconsUnits
            }
        },
        computed: {
            ...mapGetters(['getActiveStation', 'getAlarms', 'getActiveStation', 'isStaff']),
            /**
             * Dynamic translation for the name of the active station.
             */
            activeStationName() {
                if (this.getActiveStation.length === 0) {
                    return '';
                }

                if (this.$i18n.locale && this.$i18n.locale !== 'en') {
                    return `${this.getActiveStation[6]}`;
                }
                return `${this.getActiveStation[0]} #${this.getActiveStation[1]}`
            },
            /**
             * Computed latitude and longitude for the active station.
             */
            infoWindowPosition() {
                return {
                    lat: parseFloat(this.getActiveStation[2]),
                    lng: parseFloat(this.getActiveStation[3])
                };
            },
            /**
             * Check if current status is inactive. If
             */
            isInactive() {
                return parseInt(this.getActiveStation[4]) === INACTIVE_STATUS;
            },
            /**
             * Get object sensors by normalizing any ClimaVUE50 and
             * mapping the names to icons list.
             * return an Array of objects
            */
            sensors() {
                let preparedSensorsList = [];
                const dashboardObjSensors = this.getActiveStation[5];
                const isUserStaff = this.isStaff

                if (dashboardObjSensors) {
                    for (const [key, value] of Object.entries(dashboardObjSensors)) {
                        // if translation exists for metric
                        const metricTransExists = this.$i18n.te(this.iconsUnits[key]?.unit);

                        if (isUserStaff || !excludedSensors.includes(key)) {
                            preparedSensorsList.push({
                                name: key,
                                icon: this.iconsUnits[key]?.icon || DEFAULT,
                                value: this.safeTranslateValue('directions', value),
                                metric: metricTransExists ? this.$i18n.t(this.iconsUnits[key].unit) : ''
                            });
                        }
                    }

                    return preparedSensorsList;
                }
                return [];
            },
            /**
             * Get the contacts object and normalize it.
             */
            infoPoint() {
                const info = this.getActiveStation[7];

                return {
                    email: info && 'email_contact' in info ? info.email_contact : null,
                    phone: info && 'phone_contact' in info ? info.phone_contact : null,
                    lastUpdated: this.getLastUpdatedDate()
                };
            },
            /**
             * Get alarms per selected object
             */
            alarms() {
                if (
                    isEmpty(this.getActiveStation[0]) ||
                    isEmpty(this.getAlarms)
                ) {
                    return [];
                }

                return this.getAlarms
                    .filter(el => el.dev_id === this.getActiveStation[0])
                    .map(el => ({
                        icon: `attention-${iconColors[el.state]}`,
                        attr: this.safeTranslateValue('sensors', el.attrib),
                        overflow: el.overflow
                    }));
            }
        },
        methods: {
            ...mapActions([SET_CURRENT_DAM]),
            /**
             * Open sidebar info for selected dam
             * @param {Object} current active station
             */
            handleTitleClick(station) {
                this.SET_CURRENT_DAM(station);
            },
            /**
             * Get last updated date for the entire object
             */
            getLastUpdatedDate() {
                const lastUpdatedInfo = this.getActiveStation[8];
                let formattedLastUpdated;

                if (lastUpdatedInfo) {
                    try {
                        const lastUpdatedAsDate = parsedDate(lastUpdatedInfo);
                        formattedLastUpdated = formatDate(lastUpdatedAsDate);
                    }
                    catch (e) {
                        formattedLastUpdated = null;
                    }
                }

                return formattedLastUpdated;
            },
            /**
             * Get the alarm text by attribute or
             * return that is overflowing.
             */
            alarmText(iconKey) {
                if (iconKey.attr === OVERFLOWING_WATER_LEVEL) {
                    return this.$i18n.t('dashboard.infoWindow.overflow-message', {meters: iconKey.overflow})
                } else {

                    if (this.$i18n.te(`dashboard.${iconKey.attr}`)) {
                        return this.$i18n.t(`dashboard.${iconKey.attr}`);
                    } else {
                        return iconKey.attr;
                    }
                }
            }
        }
    }
</script>

<style lang="scss">
    @import "@/assets/scss/_variables.scss";

    .gm-ui-hover-effect {
        top: 0!important;
        right: 0!important;
    }

    .gm-style {
        font-family: $font-family-base;

        &-iw {
            width: $info-window-width; // sm: 259
        }
    }

    .gm-style-iw-d {
        overflow: auto !important;
    }

    .info-window {
        &-title {
            cursor: pointer;
            color: $info-window-color;
            font-size: $info-window-title-font-size;
        }

        &-hr {
            border-top: 1px solid $info-window-color;
        }

        .popup-small-grey-text {
            font-size: $font-size-v-sm;
            color: $semiDarkGrey;
            font-family: $font-family-base;
        }

        &-btn {
            margin: 0 !important;
            padding: $info-window-button-padding !important;
            &:nth-last-child(1) {
                margin-left: $info-window-button-margin !important;
            }

            .i-button-text {
                color: $info-window-color !important;
                font-size: $info-window-button-font-size !important;
                font-weight: $info-window-button-font-weight !important;
            }
        }
    }
</style>